import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnhancedCompany } from '../../contexts/EnhancedCompanyContext';
import { CompanyManager } from '../../utils/enhancedFirebaseUtils';
import { auth } from '../../firebase.config';
import { defaultTermsTemplates } from '../../utils/termsTemplates';
import './wizard-animations.css';

// Wizard Steps
import BasicInfoStep from './steps/BasicInfoStep';
import AddressStep from './steps/AddressStep';
import BankDetailsStep from './steps/BankDetailsStep';
import DocumentsStep from './steps/DocumentsStep';
import CompanySettingsStep from './steps/CompanySettingsStep';
import SubscriptionStep from './steps/SubscriptionStep';
import TermsSettingsStep from './steps/TermsSettingsStep';

// Components
import ProgressBar from './components/ProgressBar';
import StepNavigation from './components/StepNavigation';
import Toast from './components/Toast';
import { useErrorHandler } from '../../hooks/useErrorHandler';

const WIZARD_STEPS = [
  { id: 'basic', title: 'Business Info', component: BasicInfoStep },
  { id: 'address', title: 'Address', component: AddressStep },
  { id: 'bank', title: 'Bank Details', component: BankDetailsStep },
  { id: 'documents', title: 'Documents', component: DocumentsStep },
  { id: 'settings', title: 'Company Settings', component: CompanySettingsStep },
  { id: 'subscription', title: 'Choose Plan', component: SubscriptionStep },
  { id: 'terms', title: 'Terms & Conditions', component: TermsSettingsStep }
];

const CompanyWizard = ({ isOpen, onClose, mode = 'create', existingData = {}, onSubmit, startStep = 0 }) => {
  const navigate = useNavigate();
  const { createCompany, loadUserCompanies } = useEnhancedCompany();
  const { handleValidationError, handleError } = useErrorHandler();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(startStep);
  const [wizardData, setWizardData] = useState({
    // Basic Info
    firmName: '',
    companyType: '',
    businessType: '',
    contactPerson: '',
    gstinType: '',
    gstin: '',
    email: auth.currentUser?.email || '',
    contactNumber: auth.currentUser?.phoneNumber || '',
    pan: '',
    
    // Address
    address: '',
    country: 'India',
    city: '',
    state: '',
    pincode: '',
    
    // Bank Details
    bankName: '',
    bankAccount: '',
    bankAccountConfirm: '',
    bankIfsc: '',
    upiId: '',
    upiQrUrl: '',
    
    // Documents
    logoUrl: '',
    sealUrl: '',
    signUrl: '',
    
    // Company Settings
    currency: 'INR',
    gstCalculation: 'excluding',
    
    // Subscription
    selectedPlan: 'basic',
    
    // Terms
    salesTerms: defaultTermsTemplates.salesTerms,
    purchaseTerms: defaultTermsTemplates.purchaseTerms,
    quotationTerms: defaultTermsTemplates.quotationTerms,
    footer: defaultTermsTemplates.footer,
    
    ...existingData
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [hasValidated, setHasValidated] = useState(false);
  const [toast, setToast] = useState(null);

  // Update current step when startStep changes (for section-wise editing)
  useEffect(() => {
    setCurrentStep(startStep);
  }, [startStep]);

  // Clear all errors when wizard opens or when mode changes
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setHasValidated(false);
    }
  }, [isOpen, mode]);

  // Real-time validation: Re-validate if there are errors and data changes
  useEffect(() => {
    // Only re-validate if we're on step 0 and there are existing errors
    if (currentStep === 0 && Object.keys(errors).length > 0 && hasValidated) {
      // Silent validation - don't show alert
      const stepErrors = {};
      
      // Run same validation logic
      if (!wizardData.firmName || !wizardData.firmName.trim()) {
        stepErrors.firmName = 'Company name is required';
      }
      if (!wizardData.companyType) {
        stepErrors.companyType = 'Company type is required';
      }
      if (!wizardData.businessType) {
        stepErrors.businessType = 'Business type is required';
      }
      if (!wizardData.contactPerson || !wizardData.contactPerson.trim()) {
        stepErrors.contactPerson = 'Contact person is required';
      }
      if (!wizardData.gstinType) {
        stepErrors.gstinType = 'GST type is required';
      }
      
      // GST validation
      if (wizardData.gstinType === 'Regular') {
        if (!wizardData.gstin || !wizardData.gstin.trim()) {
          stepErrors.gstin = 'GST number is required for Regular GST type';
        }
      } else if (wizardData.gstinType === 'Composition') {
        if (!wizardData.gstin || !wizardData.gstin.trim()) {
          stepErrors.gstin = 'GST number is required for Composition GST type';
        }
      }
      
      // Email validation
      if (!wizardData.email || !wizardData.email.trim()) {
        stepErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(wizardData.email)) {
        stepErrors.email = 'Invalid email format';
      }
      
      // Phone validation
      const phoneValue = wizardData.contactNumber?.toString().trim() || '';
      if (!phoneValue || phoneValue.length === 0) {
        stepErrors.contactNumber = 'Contact number is required';
      }
      
      // Only update if errors actually changed
      const newErrorKeys = Object.keys(stepErrors).sort().join(',');
      const oldErrorKeys = Object.keys(errors).sort().join(',');
      if (newErrorKeys !== oldErrorKeys) {
        setErrors(stepErrors);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardData, currentStep, hasValidated]);

  // Auto-save draft data with debounce to prevent typing lag
  useEffect(() => {
    const draftKey = `company_wizard_draft_${auth.currentUser?.uid}`;
    const timeoutId = setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify(wizardData));
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [wizardData]);

  // Load draft data on mount or existing data for edit mode
  useEffect(() => {
    if (mode === 'create') {
      const draftKey = `company_wizard_draft_${auth.currentUser?.uid}`;
      const draftData = localStorage.getItem(draftKey);
      if (draftData) {
        try {
          const parsed = JSON.parse(draftData);
          setWizardData(prev => ({ ...prev, ...parsed }));
        } catch (e) {
          console.warn('Failed to load draft data:', e);
        }
      }
    } else if (mode === 'edit' && existingData && Object.keys(existingData).length > 0) {
      // For edit mode, load existing data only once
      setWizardData(prev => ({ ...prev, ...existingData }));
      // Clear any stale errors from previous sessions
      setErrors({});
      setHasValidated(false);
    }
  }, [mode, existingData]);

  const updateWizardData = (field, value) => {
    // Handle both flat updates and object updates
    if (typeof field === 'object' && value === undefined) {
      // Object update (e.g., { selectedPlan: 'premium' })
      setWizardData(prev => ({
        ...prev,
        ...field
      }));
      
      // Clear errors for all updated fields
      const updatedFields = Object.keys(field);
      setErrors(prev => {
        const newErrors = { ...prev };
        updatedFields.forEach(fieldKey => {
          delete newErrors[fieldKey];
        });
        
        // Errors cleared
        
        return newErrors;
      });
    } else {
      // Flat update (e.g., 'selectedPlan', 'premium')
      setWizardData(prev => ({
        ...prev,
        [field]: value
      }));
      
      // Clear error for this specific field when user types
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        
        // Errors cleared
        
        return newErrors;
      });
    }
  };

  const validateCurrentStep = () => {
    const step = WIZARD_STEPS[currentStep];
    const stepErrors = {};
    
    switch (step.id) {
      case 'basic':
        // Mandatory fields - with null/undefined safety checks
        if (!wizardData.firmName || !wizardData.firmName.trim()) {
          stepErrors.firmName = 'Company name is required';
        }
        if (!wizardData.companyType) {
          stepErrors.companyType = 'Company type is required';
        }
        if (!wizardData.businessType) {
          stepErrors.businessType = 'Business type is required';
        }
        if (!wizardData.contactPerson || !wizardData.contactPerson.trim()) {
          stepErrors.contactPerson = 'Contact person is required';
        }
        if (!wizardData.gstinType) {
          stepErrors.gstinType = 'GST type is required';
        }
        
        // GST validation - with null/undefined safety
        if (wizardData.gstinType === 'Regular') {
          if (!wizardData.gstin || !wizardData.gstin.trim()) {
            stepErrors.gstin = 'GST number is required for Regular GST type';
          }
        } else if (wizardData.gstinType === 'Composition') {
          if (!wizardData.gstin || !wizardData.gstin.trim()) {
            stepErrors.gstin = 'GST number is required for Composition GST type';
          }
        }
        
        // Email validation - with null/undefined safety
        if (!wizardData.email || !wizardData.email.trim()) {
          stepErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(wizardData.email)) {
          stepErrors.email = 'Invalid email format';
        }
        
        // Phone validation - with null/undefined safety and extra whitespace handling
        const phoneValue = wizardData.contactNumber?.toString().trim() || '';
        if (!phoneValue || phoneValue.length === 0) {
          stepErrors.contactNumber = 'Contact number is required';
        }
        
        // GSTIN format validation - no validation required
        
        break;
        
      case 'address':
      case 'bank':
      case 'documents':
      case 'settings':
      case 'subscription':
      case 'terms':
        // All steps after basic info are optional - no validation required
        break;
    }
    
    setErrors(stepErrors);
    setHasValidated(true);
    return Object.keys(stepErrors).length === 0;
  };

  const nextStep = () => {
    // Only validate step 0 (basic info), allow skipping others
    if (currentStep === 0) {
      const isValid = validateCurrentStep();
      
      if (isValid) {
        if (currentStep < WIZARD_STEPS.length - 1) {
          setCurrentStep(currentStep + 1);
          // Clear errors and validation state when successfully moving to next step
          setErrors({});
          setHasValidated(false);
        }
      } else {
        // Show validation errors as notifications
        console.error('❌ Validation failed with errors:', errors);
        // Display each validation error as a notification
        Object.entries(errors).forEach(([field, message]) => {
          handleValidationError(new Error(message), {
            context: 'CompanyWizard',
            fieldName: field,
            title: 'Validation Error'
          });
        });
      }
    } else {
      // Allow moving to next step without validation for non-basic steps
      if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
        // Clear any existing errors
        setErrors({});
        setHasValidated(false);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Clear errors and validation state when moving back
      setErrors({});
      setHasValidated(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      // Validation errors are already shown as notifications in validateCurrentStep
      return;
    }
    
    setLoading(true);
    try {
      const companyInfo = {
        firmName: wizardData.firmName,
        companyType: wizardData.companyType,
        gstinType: wizardData.gstinType,
        gstin: wizardData.gstin,
        email: wizardData.email,
        contactNumber: wizardData.contactNumber,
        pan: wizardData.pan,
        address: wizardData.address,
        city: wizardData.city,
        state: wizardData.state,
        pincode: wizardData.pincode,
        country: wizardData.country,
        bankName: wizardData.bankName,
        bankAccount: wizardData.bankAccount,
        bankIfsc: wizardData.bankIfsc,
        upiId: wizardData.upiId, // Add UPI ID
        logoUrl: wizardData.logoUrl,
        sealUrl: wizardData.sealUrl,
        signUrl: wizardData.signUrl,
        upiQrUrl: wizardData.upiQrUrl,
        currency: wizardData.currency,
        gstCalculation: wizardData.gstCalculation,
        salesTerms: wizardData.salesTerms,
        purchaseTerms: wizardData.purchaseTerms,
        quotationTerms: wizardData.quotationTerms,
        footer: wizardData.footer,
        selectedPlan: wizardData.selectedPlan,
        // Add missing fields
        businessType: wizardData.businessType,
        contactPerson: wizardData.contactPerson
      };

      // Clear draft data
      const draftKey = `company_wizard_draft_${auth.currentUser?.uid}`;
      localStorage.removeItem(draftKey);
      
      // Call the parent's submit handler
      if (onSubmit) {
        await onSubmit(companyInfo);
      } else {
        // Fallback to local implementation
        const result = await createCompanyWithSubscription(companyInfo, wizardData.selectedPlan);
        
        if (result.success) {
          // Reload user companies
          await loadUserCompanies(auth.currentUser.uid);
          
          // Close wizard and redirect
          onClose();
          navigate('/dashboard');
          
          // Show success toast
          setToast({
            message: `Company "${wizardData.firmName}" created successfully!`,
            type: 'success'
          });
        }
      }
    } catch (error) {
      console.error('Error creating company:', error);
      // Show notification using error handler
      handleError(error, {
        title: 'Company Creation Failed',
        context: 'CompanyWizard'
      });
      // Also show toast for inline display
      setToast({
        message: `Failed to create company: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Save current progress and exit - validate only if on basic info step
  const handleSaveAndExit = async () => {
    // Only validate if we're on the basic info step (step 0)
    if (currentStep === 0) {
      if (!validateCurrentStep()) {
        return;
      }
    }
    
    setLoading(true);
    try {
      const companyInfo = {
        firmName: wizardData.firmName,
        companyType: wizardData.companyType,
        gstinType: wizardData.gstinType,
        gstin: wizardData.gstin,
        email: wizardData.email,
        contactNumber: wizardData.contactNumber,
        pan: wizardData.pan,
        address: wizardData.address,
        city: wizardData.city,
        state: wizardData.state,
        pincode: wizardData.pincode,
        country: wizardData.country,
        bankName: wizardData.bankName,
        bankAccount: wizardData.bankAccount,
        bankIfsc: wizardData.bankIfsc,
        upiId: wizardData.upiId, // Add UPI ID
        logoUrl: wizardData.logoUrl,
        sealUrl: wizardData.sealUrl,
        signUrl: wizardData.signUrl,
        upiQrUrl: wizardData.upiQrUrl,
        currency: wizardData.currency,
        gstCalculation: wizardData.gstCalculation,
        salesTerms: wizardData.salesTerms,
        purchaseTerms: wizardData.purchaseTerms,
        quotationTerms: wizardData.quotationTerms,
        footer: wizardData.footer,
        selectedPlan: wizardData.selectedPlan,
        // Add missing fields
        businessType: wizardData.businessType,
        contactPerson: wizardData.contactPerson
      };

      if (onSubmit) {
        await onSubmit(companyInfo);
      } else {
        // For create mode fallback, just persist draft and close
        const draftKey = `company_wizard_draft_${auth.currentUser?.uid}`;
        localStorage.setItem(draftKey, JSON.stringify(wizardData));
      }
    } catch (error) {
      console.error('Error saving company info (save & exit):', error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const createCompanyWithSubscription = async (companyData, selectedPlan) => {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // Create company using existing CompanyManager
    const result = await CompanyManager.createCompany(userId, companyData);
    return result;
  };

  if (!isOpen) return null;

  const CurrentStepComponent = WIZARD_STEPS[currentStep].component;
  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  return (
    <>
      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
        <div className="min-h-screen px-2 sm:px-4 md:px-6 text-center flex items-center justify-center py-4">
          <div className="inline-block w-full max-w-[800px] text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:rounded-xl max-h-[90vh] overflow-hidden flex flex-col">
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 md:p-8 border-b border-gray-200">
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 truncate">
                {mode === 'create' ? 'Create New Company' : 'Edit Company'}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                <span className="hidden sm:inline">Step {currentStep + 1} of {WIZARD_STEPS.length}: </span>
                <span className="sm:hidden">{currentStep + 1}/{WIZARD_STEPS.length}: </span>
                {WIZARD_STEPS[currentStep].title}
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-3 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 z-10"
              title="Close"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <ProgressBar 
            currentStep={currentStep} 
            totalSteps={WIZARD_STEPS.length}
            steps={WIZARD_STEPS}
          />

          {/* Step Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 wizard-content" style={{maxHeight: 'calc(90vh - 140px)'}}>
            {/* Validation Errors - Show at top */}
            
            <CurrentStepComponent
              data={wizardData}
              errors={errors}
              touched={touched}
              onChange={updateWizardData}
              onTouch={(field) => {
                // Skip validation for terms fields
                if (field.includes('Terms') || field === 'footer') {
                  return;
                }
                setTouched(prev => ({ ...prev, [field]: true }));
              }}
            />
          </div>

          {/* Navigation */}
          <StepNavigation
            currentStep={currentStep}
            totalSteps={WIZARD_STEPS.length}
            onPrev={prevStep}
            onNext={nextStep}
            onSubmit={handleSubmit}
            loading={loading}
            canProceed={currentStep === 0 ? Object.keys(errors).length === 0 : true}
            onSkip={nextStep}
            mode={mode}
            onSaveAndExit={handleSaveAndExit}
          />
        </div>
      </div>
    </div>
    </>
  );
};

export default CompanyWizard;
